import classNames from 'classnames';
import { FullPost } from 'model';
import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { RootState } from '../App';
import { EventLikeDislike, PostLikeDislike } from '../components/LikeDislike';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { useShare } from '../hooks/useShare';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { LoginState } from '../state/loginSlice';
import { IconButton } from './IconButton';
import { PostTypeView } from './PostTypeView/PostTypeView';
import { PromotedView } from './PostTypeView/PromotedView.js';
import { IconChatbox, IconShare } from './QuirkIcons';

// styles
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';
import { StatsBar } from './StatsBar.js';
import { getBackendURL, useBackendDomain } from '../hooks/useBackendDomain.js';

export const PostItem: React.FC<{
	post: FullPost;
	detail?: boolean;
	hidebuttonset?: boolean;
	expanded?: boolean;
	screened?: boolean;
}> = ({ post, detail, hidebuttonset, expanded, screened }) => {
	const [domain] = useBackendDomain();
	const { handleShare } = useShare({
		message: getBackendURL('/api/sharing/' + post._id, domain || window.location.origin),
		header: 'Share this as a link'
	});
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const { displayComments, closedForComments } = post;

	let captionMessage = '';
	if (post.postType === 'marketplaceItem' && post.closed) {
		captionMessage = 'No longer available';
	} else if ((displayComments && closedForComments) || !displayComments) {
		captionMessage = 'Closed for comments';
	}
	const isAuthor = useCurrentUser()._id === post.author;
	const allowReply = !isAuthor || post.postType !== 'marketplaceItem';

	return (
		<div className={classNames(screened && postStyles.screenedOverlay)} data-testid={`PostItem-${post._id}`}>
			<PromotedView post={post} detail={detail}>
				<PostTypeView post={post} detail={detail} expanded={expanded} />
			</PromotedView>
			{!hidebuttonset && (
				<div className={postStyles.postFooterContainer}>
					{captionMessage !== '' && (
						<div className={postStyles.caption} data-testid="PostCaption">
							{captionMessage}
						</div>
					)}
					<div className={postStyles.postFooter}>
						<div className={postStyles.postFooterleft}>
							{displayComments && (
								<>
									{!closedForComments && allowReply && (
										<NavLink
											to={`/main/latest/post/reply/${post._id}/${detail ? 'detail' : 'feed'}`}
											className="nounderline"
										>
											<IconButton
												icon={<IconChatbox size="large" />}
												testId={`PostReplyButton-${post._id}`}
											/>
										</NavLink>
									)}

									<IconButton icon={<IconShare size="large" />} onClick={handleShare} />
								</>
							)}
						</div>
						<div className={postStyles.postFooterright}>
							<RealUserOnly>
								<>
									{post.postType === 'event' ? (
										<>
											{state.user._id !== post.author && !detail ? (
												<EventLikeDislike likeable={post} />
											) : null}
										</>
									) : (
										<>
											{state.user._id !== post.author && !detail ? (
												<PostLikeDislike likeable={post} />
											) : null}
										</>
									)}
								</>
							</RealUserOnly>
						</div>
					</div>

					<StatsBar post={post} hideEmptyComments={detail} />
				</div>
			)}
		</div>
	);
};
